import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import type { Dayjs } from 'dayjs';
import {
  $accountPaymentListStore,
  $reportStore,
  $accountPaymentReportStore,
  $paymentStore,
  $profileFeatureStore,
  $initialCurrenciesAvailableStore,
} from '@lib/store';
import { FormatUtils, LocalStorageUtils } from '@shared/utils';
import { type AccountResponse, AccountType } from '@shared/api/rest/accounts';
import type { GetDepositPaymentsByAccountsRequest } from '@shared/api/rest/payment';
import { type GeneratePaymentReportRequest, PaymentReportColumnsEnum, ReportExportFileType } from '@shared/api/rest/report';
import { ReportSecondaryTabs } from '@pages/reports/models';
import { Select, SelectDateRange } from '@shared/widgets/select';
import { RadioInputOrientationTypeEnum } from '@shared/widgets/inputs/radio/models';
import { DepositAccountTable, DepositTransactionTable } from './';
import { ColumnsModal } from '../columnsModal';
import { GeneratedFileModal } from '../generatedFileModal';
import { Button, Icon, Modal, NoData, RadioInput, SearchInput, Spinner } from '@shared/widgets';
import DownloadIcon from '@assets/icons/download.svg';
import PlusIcon from '@assets/icons/plus.svg';

const allRadioOptions = Object.entries(ReportExportFileType).map((x) => ({ value: x[0], text: x[1] }));
const allSecondaryTabs = Object.entries(ReportSecondaryTabs).map((x) => ({ label: x[1], value: x[1] }));

enum ColumnsEnum {
  Status = 'Status',
  ReferenceNumber = 'ReferenceNumber',
  Date = 'Date',
  AccountNumber = 'AccountNumber',
  Direction = 'Direction',
  TypeOfTransfer = 'TypeOfTransfer',
  Description = 'Description',
  SenderIban = 'SenderIban',
  BeneficiaryIban = 'BeneficiaryIban',
  ExchangeRate = 'ExchangeRate',
  SenderAmount = 'SenderAmount',
  BeneficiaryAmount = 'BeneficiaryAmount',
  Fee = 'Fee',
  TotalAmount = 'TotalAmount',
}

const COLUMNS = [
  ColumnsEnum.Status,
  ColumnsEnum.ReferenceNumber,
  ColumnsEnum.Date,
  ColumnsEnum.AccountNumber,
  ColumnsEnum.Direction,
  ColumnsEnum.TypeOfTransfer,
  ColumnsEnum.Description,
  ColumnsEnum.SenderIban,
  ColumnsEnum.BeneficiaryIban,
  ColumnsEnum.ExchangeRate,
  ColumnsEnum.SenderAmount,
  ColumnsEnum.BeneficiaryAmount,
  ColumnsEnum.Fee,
  ColumnsEnum.TotalAmount,
];

const mapColumnToEnum = (column: string): PaymentReportColumnsEnum | undefined => {
  const columns: { [key: string]: PaymentReportColumnsEnum } = {
    [ColumnsEnum.Status]: PaymentReportColumnsEnum.Status,
    [ColumnsEnum.ReferenceNumber]: PaymentReportColumnsEnum.ReferenceNumber,
    [ColumnsEnum.Date]: PaymentReportColumnsEnum.Date,
    [ColumnsEnum.AccountNumber]: PaymentReportColumnsEnum.AccountNumber,
    [ColumnsEnum.Direction]: PaymentReportColumnsEnum.Direction,
    [ColumnsEnum.TypeOfTransfer]: PaymentReportColumnsEnum.TypeOfTransfer,
    [ColumnsEnum.Description]: PaymentReportColumnsEnum.Description,
    [ColumnsEnum.SenderIban]: PaymentReportColumnsEnum.SenderIban,
    [ColumnsEnum.BeneficiaryIban]: PaymentReportColumnsEnum.BeneficiaryIban,
    [ColumnsEnum.ExchangeRate]: PaymentReportColumnsEnum.ExchangeRate,
    [ColumnsEnum.SenderAmount]: PaymentReportColumnsEnum.SenderAmount,
    [ColumnsEnum.BeneficiaryAmount]: PaymentReportColumnsEnum.BeneficiaryAmount,
    [ColumnsEnum.Fee]: PaymentReportColumnsEnum.Fee,
    [ColumnsEnum.TotalAmount]: PaymentReportColumnsEnum.TotalAmount,
  };

  return columns[column];
};

export const ReportDepositTab = () => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const paymentStore = useUnit($paymentStore.store);
  const accountsStore = useUnit($accountPaymentListStore.store);
  const accountReportStore = useUnit($accountPaymentReportStore.store);
  const reportStore = useUnit($reportStore.store);

  const [searchText, setSearchText] = useState('');
  const [showGeneratedModal, setShowGeneratedModal] = useState<boolean>(false);
  const [secondaryTab, setSecondaryTab] = useState<ReportSecondaryTabs>(ReportSecondaryTabs.Transactions);
  const isTransactionTab = secondaryTab === ReportSecondaryTabs.Transactions;

  const columns = LocalStorageUtils.getPaymentTransactionColumns();
  const [isOpenFieldsModal, setIsOpenFieldsModal] = useState<boolean>(false);
  const [transactionColumns, setTransactionColumns] = useState<string[]>(columns || COLUMNS);
  const updateColumns = (activeColumns: string[]) => {
    setTransactionColumns(activeColumns);
    LocalStorageUtils.setPaymentTransactionColumns(activeColumns);
  };

  const onChangeSecondaryTab = (tab: ReportSecondaryTabs) => {
    setSecondaryTab(tab);
    setCurrency(undefined);
  };

  const [currency, setCurrency] = useState<string>();
  const [account, setAccount] = useState<AccountResponse | undefined>(undefined);
  const [dateRange, setDateRange] = useState<{ dateFrom?: Dayjs; dateTo?: Dayjs } | undefined>(undefined);
  const [isOpenExportModal, setIsOpenExportModal] = useState(false);

  const [accountCurrentPage, setAccountCurrentPage] = useState(1);
  const [accountCountItemPage, setAccountCountItemPage] = useState(10);
  const [transactionCurrentPage, setTransactionCurrentPage] = useState(1);
  const [transactionCountItemPage, setTransactionCountItemPage] = useState(10);

  const updateAccounts = () => {
    $accountPaymentReportStore.events.update({
      Currency: currency,
      ReturnTotalCount: true,
      Count: accountCountItemPage,
      FromIndex: (accountCurrentPage - 1) * accountCountItemPage,
    });
  };

  const updateTransactions = () => {
    if (accountsStore.data.length <= 0) return;
    $paymentStore.events.getPaymentsByAccounts({
      AccountIds: account ? [account.id] : accountsStore.data.map((x) => x.id),
      Currency: currency,
      DateFrom: dateRange?.dateFrom?.toISOString(),
      DateTo: dateRange?.dateTo?.toISOString(),
      SearchText: searchText,
      ReturnTotalCount: true,
      Count: transactionCountItemPage,
      FromIndex: (transactionCurrentPage - 1) * transactionCountItemPage,
    });
  };

  const onExport = (item: ReportExportFileType) => {
    setIsOpenExportModal(false);
    if (isTransactionTab) {
      const columnsVisible = transactionColumns.map(mapColumnToEnum).filter((col) => !!col);
      const request: GeneratePaymentReportRequest = {
        reportType: item,
        columnsVisible,
        accountIds: account ? [account.id] : accountsStore.data.map((x) => x.id),
        currency: currency,
        searchText: searchText,
        dateFrom: dateRange?.dateFrom?.toISOString(),
        dateTo: dateRange?.dateTo?.toISOString(),
      };
      $reportStore.events.generatePaymentReport(request);
    } else {
      $reportStore.events.generatePaymentAccountBalanceReport({
        ReportType: item,
        Currency: currency,
      });
    }
  };

  useEffect(() => {
    if (!reportStore.loading && reportStore.data) setShowGeneratedModal(true);
  }, [reportStore.data, reportStore.loading]);

  useEffect(() => {
    if (secondaryTab !== ReportSecondaryTabs.AccountsBalance) return;
    if (!profileFeatureStore.data?.reports.viewAccountBalanceReport) return;
    updateAccounts();
  }, [secondaryTab, currency, accountCountItemPage, accountCurrentPage]);

  useEffect(() => {
    if (secondaryTab !== ReportSecondaryTabs.Transactions) return;
    updateTransactions();
  }, [secondaryTab, currency, searchText, account, dateRange, transactionCountItemPage, transactionCurrentPage]);

  useEffect(() => {
    if (accountsStore.data.length <= 0) return;
    const request: GetDepositPaymentsByAccountsRequest = {
      AccountIds: accountsStore.data.filter((x) => x.type === AccountType.Payment).map((x) => x.id),
    };
    if (request.AccountIds.length <= 0) return;
    $paymentStore.events.getPaymentsByAccounts(request);
  }, [accountsStore]);

  useEffect(() => {
    $accountPaymentListStore.events.update({ AccountType: AccountType.Payment });

    if (profileFeatureStore.data?.reports.viewAccountBalanceReport) {
      $accountPaymentReportStore.events.update({ Count: 100 });
    }
    // $cardListStore.events.update({ Count: 100 });

    return () => {
      $accountPaymentReportStore.events.clear();
      $accountPaymentListStore.events.clear();
      $paymentStore.events.clear();
    };
  }, []);

  return (
    <div className='w-full h-full flex flex-col'>
      <div className='flex my-6'>
        {isTransactionTab && <SearchInput value={searchText} onChange={setSearchText} />}
        <div className='ml-auto flex gap-2'>
          {isTransactionTab && (
            <Button onClick={() => setIsOpenFieldsModal(true)} color='primary'>
              <Icon icon={<PlusIcon />} color='inherit' />
              Add filter
            </Button>
          )}
          <Button
            onClick={() => setIsOpenExportModal(true)}
            color='primary'
            disabled={isTransactionTab ? !paymentStore.data.data.length : !accountReportStore.data.data.length}
          >
            <Icon icon={<DownloadIcon />} color='inherit' />
            Export
          </Button>
        </div>
      </div>
      <div className='flex justify-between'>
        <div className='flex'>
          {allSecondaryTabs
            .filter(
              (x) =>
                x.value === ReportSecondaryTabs.Transactions ||
                (profileFeatureStore.data?.reports.viewAccountBalanceReport && x.value === ReportSecondaryTabs.AccountsBalance),
            )
            .map((x, key) => (
              <div
                className={clsx(
                  'p-4 font-semibold cursor-pointer',
                  x.value == secondaryTab
                    ? 'bg-white dark:bg-soft-black text-soft-black dark:text-white'
                    : 'bg-transparent text-slate dark:text-white dark:opacity-70',
                )}
                key={key}
                onClick={() => onChangeSecondaryTab(x.value)}
              >
                {x.label}
              </div>
            ))}
        </div>
        <div className='flex my-auto mb-6'>
          <Select
            className='min-w-[160px]'
            items={currenciesAvailableStore.data}
            nullValue={{ isEnable: true, text: 'All currencies' }}
            value={currency}
            onChange={setCurrency}
          />
          {isTransactionTab && (
            <>
              <Select
                className='ml-6'
                items={accountsStore.data.map((x) => ({ label: FormatUtils.formatAccountNumber(x.accountNumber) || '', value: x }))}
                nullValue={{ isEnable: true, text: 'All accounts' }}
                value={account}
                onChange={setAccount}
              />
              <SelectDateRange className='ml-6' value={dateRange} onChange={setDateRange} />
            </>
          )}
        </div>
      </div>
      <div className='flex w-full'>
        {secondaryTab === ReportSecondaryTabs.AccountsBalance ? (
          <>
            {profileFeatureStore.data?.reports.viewAccountBalanceReport && (
              <DepositAccountTable
                isLoading={accountReportStore.loading}
                accounts={accountReportStore.data.data}
                currentPage={accountCurrentPage}
                totalItemCount={accountReportStore.data.total}
                countItemPage={accountCountItemPage}
                onChangePage={setAccountCurrentPage}
                onChangeCountItemPage={setAccountCountItemPage}
              />
            )}
          </>
        ) : (
          <>
            {profileFeatureStore.data ? (
              profileFeatureStore.data.accountFlows.length > 0 ? (
                <>
                  {profileFeatureStore.data?.reports.viewTransactions && (
                    <DepositTransactionTable
                      isLoading={paymentStore.loading || accountsStore.loading}
                      columns={transactionColumns}
                      items={paymentStore.data.data}
                      currentPage={transactionCurrentPage}
                      totalItemCount={paymentStore.data.total}
                      countItemPage={transactionCountItemPage}
                      onChangePage={setTransactionCurrentPage}
                      onChangeCountItemPage={setTransactionCountItemPage}
                    />
                  )}
                </>
              ) : (
                <NoData text='No related records' />
              )
            ) : (
              ''
            )}
          </>
        )}
      </div>
      <Modal isOpen={isOpenExportModal} title='Download' onClose={() => setIsOpenExportModal(false)}>
        <div className='flex flex-col'>
          <RadioInput
            options={allRadioOptions}
            onChange={(val) => onExport(val as ReportExportFileType)}
            orientation={RadioInputOrientationTypeEnum.Vertical}
          />
        </div>
      </Modal>
      {reportStore.loading && <Spinner position />}
      <ColumnsModal
        show={isOpenFieldsModal}
        onClose={() => setIsOpenFieldsModal(false)}
        onSave={updateColumns}
        columns={COLUMNS}
        activeColumns={transactionColumns}
      />
      <GeneratedFileModal open={showGeneratedModal} onClose={() => setShowGeneratedModal(false)} />
    </div>
  );
};
